import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import styles from './RequestScheduleModal.module.scss';
import { useTranslation } from 'react-i18next';

export interface RequestScheduleModalRef {
    save: () => Promise<void>;
    isDirty: () => boolean;
    reset: () => void;
}

interface RequestScheduleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (schedule: any) => Promise<void>;
    initialSchedule?: any;
    hideCloseButton?: boolean;
    onDirtyChange?: (isDirty: boolean) => void;
}

const getDays = (t: any) => [
    { value: 1, label: t('appAdmin.RequestSchedule.days.monday', 'Lunes'), short: t('appAdmin.RequestSchedule.days.mondayShort', 'Lun') },
    { value: 2, label: t('appAdmin.RequestSchedule.days.tuesday', 'Martes'), short: t('appAdmin.RequestSchedule.days.tuesdayShort', 'Mar') },
    { value: 3, label: t('appAdmin.RequestSchedule.days.wednesday', 'Miércoles'), short: t('appAdmin.RequestSchedule.days.wednesdayShort', 'Mié') },
    { value: 4, label: t('appAdmin.RequestSchedule.days.thursday', 'Jueves'), short: t('appAdmin.RequestSchedule.days.thursdayShort', 'Jue') },
    { value: 5, label: t('appAdmin.RequestSchedule.days.friday', 'Viernes'), short: t('appAdmin.RequestSchedule.days.fridayShort', 'Vie') },
    { value: 6, label: t('appAdmin.RequestSchedule.days.saturday', 'Sábado'), short: t('appAdmin.RequestSchedule.days.saturdayShort', 'Sáb') },
    { value: 0, label: t('appAdmin.RequestSchedule.days.sunday', 'Domingo'), short: t('appAdmin.RequestSchedule.days.sundayShort', 'Dom') }
];

const getDefaultDaySchedule = () => ({
    1: { active: true, start: '09:00', end: '18:00' },
    2: { active: true, start: '13:00', end: '18:00' },
    3: { active: true, start: '09:00', end: '18:00' },
    4: { active: true, start: '09:00', end: '18:00' },
    5: { active: true, start: '09:00', end: '18:00' },
    6: { active: true, start: '09:00', end: '12:00' },
    0: { active: false, start: '09:00', end: '12:00' }
});

const TimePickerPopover = ({ 
    activePicker, 
    onClose, 
    onChange 
}: { 
    activePicker: { rect: DOMRect, value: string } | null, 
    onClose: () => void, 
    onChange: (val: string) => void 
}) => {
    if (!activePicker) return null;

    const [hour, minute] = (activePicker.value || '09:00').split(':');

    const rect = activePicker.rect;
    const width = 122;
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(Math.max(8, rect.left), maxLeft);
    const below = rect.bottom + 4;
    const estimatedHeight = 260;
    const top = below + estimatedHeight <= window.innerHeight - 8
        ? below
        : Math.max(8, rect.top - estimatedHeight - 4);

    return (
        <>
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }} onClick={onClose} />
            <div className={styles['bw-time-popover']} style={{ left, top }}>
                <div className={styles['bw-time-column']}>
                    {Array.from({ length: 24 }).map((_, i) => {
                        const val = String(i).padStart(2, '0');
                        return (
                            <button
                                key={`h-${val}`}
                                type="button"
                                className={`${styles['bw-time-option']} ${val === hour ? styles['is-selected'] : ''}`}
                                onClick={() => onChange(`${val}:${minute}`)}
                                ref={val === hour ? (el) => el?.scrollIntoView({ block: 'center' }) : undefined}
                            >
                                {val}
                            </button>
                        );
                    })}
                </div>
                <div className={styles['bw-time-column']}>
                    {Array.from({ length: 12 }).map((_, i) => {
                        const val = String(i * 5).padStart(2, '0');
                        return (
                            <button
                                key={`m-${val}`}
                                type="button"
                                className={`${styles['bw-time-option']} ${val === minute ? styles['is-selected'] : ''}`}
                                onClick={() => { onChange(`${hour}:${val}`); onClose(); }}
                                ref={val === minute ? (el) => el?.scrollIntoView({ block: 'center' }) : undefined}
                            >
                                {val}
                            </button>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export const RequestScheduleModal = forwardRef<RequestScheduleModalRef, RequestScheduleModalProps>(({ 
    isOpen, 
    onClose, 
    onSave, 
    initialSchedule, 
    hideCloseButton,
    onDirtyChange
}, ref) => {
    const { t } = useTranslation();
    const DAYS = useMemo(() => getDays(t), [t]);

    const [tab, setTab] = useState<'block' | 'day'>('day');
    
    // Block state
    const [blockDays, setBlockDays] = useState<number[]>([1, 2, 3, 4, 5]);
    const [blockStart, setBlockStart] = useState('09:00');
    const [blockEnd, setBlockEnd] = useState('18:00');
    const [isBlockApplied, setIsBlockApplied] = useState(false);

    // Day by day state
    const [daySchedule, setDaySchedule] = useState<{ [key: number]: { active: boolean; start: string; end: string } }>(getDefaultDaySchedule());
    const [initialSnapshot, setInitialSnapshot] = useState<string>('');

    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [activePicker, setActivePicker] = useState<{ type: string, day?: number, rect: DOMRect, value: string } | null>(null);

    const parseInitialData = (init: any) => {
        let initialTab: 'block' | 'day' = 'day';
        let initialBlockDays = [1, 2, 3, 4, 5];
        let initialBlockStart = '09:00';
        let initialBlockEnd = '18:00';
        let initialDaySchedule: { [key: number]: { active: boolean; start: string; end: string } } = getDefaultDaySchedule();

        if (init) {
            if (init.type === 'block') {
                initialTab = 'block';
                if (Array.isArray(init.days)) initialBlockDays = init.days;
                if (init.start) initialBlockStart = init.start;
                if (init.end) initialBlockEnd = init.end;
            } else if (init.type === 'day' && init.schedule) {
                initialTab = 'day';
                initialDaySchedule = { ...getDefaultDaySchedule(), ...init.schedule };
            } else if (Array.isArray(init)) {
                initialTab = 'day';
                const sched: { [key: number]: { active: boolean; start: string; end: string } } = { ...getDefaultDaySchedule() };
                init.forEach((item: any) => {
                    const dayNum = Number(item.day);
                    sched[dayNum] = {
                        active: Boolean(item.enabled),
                        start: item.from || '09:00',
                        end: item.to || '18:00'
                    };
                });
                initialDaySchedule = sched;
            }
        }

        return {
            tab: initialTab,
            blockDays: initialBlockDays,
            blockStart: initialBlockStart,
            blockEnd: initialBlockEnd,
            daySchedule: initialDaySchedule
        };
    };

    useEffect(() => {
        if (isOpen) {
            const parsed = parseInitialData(initialSchedule);
            setTab(parsed.tab);
            setBlockDays(parsed.blockDays);
            setBlockStart(parsed.blockStart);
            setBlockEnd(parsed.blockEnd);
            setDaySchedule(parsed.daySchedule);
            const snapshotStr = JSON.stringify({
                tab: parsed.tab,
                blockDays: [...parsed.blockDays].sort(),
                blockStart: parsed.blockStart,
                blockEnd: parsed.blockEnd,
                daySchedule: parsed.daySchedule
            });
            setInitialSnapshot(snapshotStr);
            setIsConfirmed(false);
            setIsBlockApplied(false);
            onDirtyChange?.(false);
        }
    }, [isOpen, initialSchedule]);

    const currentSnapshotStr = useMemo(() => {
        return JSON.stringify({
            tab,
            blockDays: [...blockDays].sort(),
            blockStart,
            blockEnd,
            daySchedule
        });
    }, [tab, blockDays, blockStart, blockEnd, daySchedule]);

    const isCurrentDirty = initialSnapshot !== '' && currentSnapshotStr !== initialSnapshot;

    useEffect(() => {
        onDirtyChange?.(isCurrentDirty);
    }, [isCurrentDirty, onDirtyChange]);

    const getCurrentPayload = () => {
        if (tab === 'block') {
            return {
                type: 'block',
                days: blockDays,
                start: blockStart,
                end: blockEnd
            };
        }
        return {
            type: 'day',
            schedule: daySchedule
        };
    };

    useImperativeHandle(ref, () => ({
        save: async () => {
            const payload = getCurrentPayload();
            setIsSaving(true);
            try {
                await onSave(payload);
                setInitialSnapshot(currentSnapshotStr);
                onDirtyChange?.(false);
                setIsConfirmed(true);
            } finally {
                setIsSaving(false);
            }
        },
        isDirty: () => isCurrentDirty,
        reset: () => {
            const parsed = parseInitialData(initialSchedule);
            setTab(parsed.tab);
            setBlockDays(parsed.blockDays);
            setBlockStart(parsed.blockStart);
            setBlockEnd(parsed.blockEnd);
            setDaySchedule(parsed.daySchedule);
            setIsConfirmed(false);
            onDirtyChange?.(false);
        }
    }));

    if (!isOpen) return null;

    const generateScheduleSummary = () => {
        if (tab === 'block') {
            if (blockDays.length === 0) return 'Sin días configurados';
            const sortedDays = [...blockDays].sort((a, b) => {
                const order = [1, 2, 3, 4, 5, 6, 0];
                return order.indexOf(a) - order.indexOf(b);
            });
            const parts: string[] = [];
            let start = 0;
            while (start < sortedDays.length) {
                let end = start;
                const order = [1, 2, 3, 4, 5, 6, 0];
                while (end + 1 < sortedDays.length && order.indexOf(sortedDays[end + 1]) === order.indexOf(sortedDays[end]) + 1) {
                    end += 1;
                }
                const startVal = sortedDays[start];
                const endVal = sortedDays[end];
                const firstLabel = DAYS.find(d => d.value === startVal)?.short || '';
                const lastLabel = DAYS.find(d => d.value === endVal)?.short || '';
                const length = end - start + 1;
                
                if (length >= 3) parts.push(`${firstLabel}–${lastLabel}`);
                else if (length === 2) parts.push(`${firstLabel} y ${lastLabel}`);
                else parts.push(firstLabel);
                
                start = end + 1;
            }
            return (
                <span className={styles['schedule-confirmation-summary-line']}>
                    {parts.join(', ')} {blockStart}–{blockEnd}
                </span>
            );
        } else {
            const groups = new Map<string, number[]>();
            const order = [1, 2, 3, 4, 5, 6, 0];
            
            order.forEach(dayValue => {
                const config = daySchedule[dayValue];
                if (!config || !config.active) return;
                const key = `${config.start}|${config.end}`;
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key)!.push(dayValue);
            });
            
            if (groups.size === 0) return 'Sin días configurados';
            
            const summaries = Array.from(groups.entries()).map(([key, dayValues]) => {
                const [start, end] = key.split('|');
                const parts: string[] = [];
                let s = 0;
                while (s < dayValues.length) {
                    let e = s;
                    while (e + 1 < dayValues.length && order.indexOf(dayValues[e + 1]) === order.indexOf(dayValues[e]) + 1) {
                        e += 1;
                    }
                    const startVal = dayValues[s];
                    const endVal = dayValues[e];
                    const firstLabel = DAYS.find(d => d.value === startVal)?.short || '';
                    const lastLabel = DAYS.find(d => d.value === endVal)?.short || '';
                    const length = e - s + 1;
                    
                    if (length >= 3) parts.push(`${firstLabel}–${lastLabel}`);
                    else if (length === 2) parts.push(`${firstLabel} y ${lastLabel}`);
                    else parts.push(firstLabel);
                    
                    s = e + 1;
                }
                const daysStr = parts.join(', ');
                const capitalizedDaysStr = daysStr.charAt(0).toUpperCase() + daysStr.slice(1);
                return `${capitalizedDaysStr} ${start}–${end}`;
            });
            
            return (
                <>
                    {summaries.map((s, idx) => (
                        <span key={idx} className={styles['schedule-confirmation-summary-line']}>{s}</span>
                    ))}
                </>
            );
        }
    };

    const handleApplyBlock = () => {
        setDaySchedule(prev => {
            const updated = { ...prev };
            [1, 2, 3, 4, 5, 6, 0].forEach(d => {
                if (blockDays.includes(d)) {
                    updated[d] = { active: true, start: blockStart, end: blockEnd };
                }
            });
            return updated;
        });
        setIsBlockApplied(true);
        setTimeout(() => setIsBlockApplied(false), 3000);
    };

    if (isConfirmed) {
        return (
            <div className={styles['schedule-confirmation']}>
                <div className={styles['schedule-confirmation-card']}>
                    <div className={styles['schedule-confirmation-icon']}>✓</div>
                    <h5>{t('appAdmin.RequestSchedule.configDone', 'Días y horarios guardados')}</h5>
                    <p>{t('appAdmin.RequestSchedule.configNote', 'Tu disponibilidad quedó guardada así:')}</p>
                    
                    <div className={styles['schedule-confirmation-summary']}>
                        {generateScheduleSummary()}
                    </div>
                    
                    <p className={styles['schedule-confirmation-note']}>
                        {t('appAdmin.RequestSchedule.changesSaveNote', 'Los cambios de Solicitud de horario ya quedaron guardados. Podés seguir editando o salir desde la barra inferior.')}
                    </p>
                    
                    <div className={styles['schedule-confirmation-actions']}>
                        <button 
                            className={styles['schedule-confirmation-edit']} 
                            onClick={() => setIsConfirmed(false)}
                            type="button"
                        >
                            {t('appAdmin.RequestSchedule.continueEditing', 'Seguir editando')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles['availability-schedule-view']}>
            <div className={styles['schedule-inline-head']}>
                <div>
                    <h4>{t('appAdmin.RequestSchedule.title', 'Configurar días y horarios')}</h4>
                    <p>{t('appAdmin.RequestSchedule.description', 'Definí cuándo pueden proponerte una consulta. La hora exacta queda sujeta a tu confirmación.')}</p>
                </div>
                {!hideCloseButton && (
                    <button className={styles['schedule-inline-close']} type="button" onClick={onClose}>
                        {t('appAdmin.RequestSchedule.closeBtn', 'Cerrar')}
                    </button>
                )}
            </div>

            <div className={styles['schedule-tabs']} role="tablist">
                <button 
                    className={`${styles['schedule-tab']} ${tab === 'block' ? styles['is-active'] : ''}`} 
                    type="button" 
                    role="tab" 
                    onClick={() => setTab('block')}
                >
                    {t('appAdmin.RequestSchedule.tabBlock', 'Por bloque')}
                </button>
                <button 
                    className={`${styles['schedule-tab']} ${tab === 'day' ? styles['is-active'] : ''}`} 
                    type="button" 
                    role="tab" 
                    onClick={() => setTab('day')}
                >
                    {t('appAdmin.RequestSchedule.tabDay', 'Día a día')}
                </button>
            </div>

            {tab === 'block' && (
                <div className={styles['schedule-tab-panel']}>
                    <div className={styles['schedule-block-editor']}>
                        <h5>{t('appAdmin.RequestSchedule.blockTitle', 'Aplicar un rango por bloque')}</h5>
                        <p>{t('appAdmin.RequestSchedule.blockDesc', 'Seleccioná varios días y asignales el mismo horario.')}</p>
                        
                        <div className={styles['schedule-block-controls']}>
                            <div className={styles['schedule-block-days']}>
                                {DAYS.map(day => (
                                    <label className={styles['schedule-block-day']} key={day.value}>
                                        <input 
                                            type="checkbox" 
                                            checked={blockDays.includes(day.value)}
                                            onChange={(e) => {
                                                if (e.target.checked) setBlockDays([...blockDays, day.value]);
                                                else setBlockDays(blockDays.filter(d => d !== day.value));
                                            }}
                                        />
                                        <span>{day.short}</span>
                                    </label>
                                ))}
                            </div>

                            <div className={styles['schedule-block-time']}>
                                <input 
                                    type="text" 
                                    readOnly
                                    className={`${styles['schedule-time-input']} ${activePicker && (activePicker as any).type === 'blockStart' ? styles['is-active'] : ''}`} 
                                    value={blockStart} 
                                    onClick={(e) => setActivePicker({ type: 'blockStart', rect: e.currentTarget.getBoundingClientRect(), value: blockStart })}
                                />
                                <i>a</i>
                                <input 
                                    type="text" 
                                    readOnly
                                    className={`${styles['schedule-time-input']} ${activePicker && (activePicker as any).type === 'blockEnd' ? styles['is-active'] : ''}`} 
                                    value={blockEnd} 
                                    onClick={(e) => setActivePicker({ type: 'blockEnd', rect: e.currentTarget.getBoundingClientRect(), value: blockEnd })}
                                />
                            </div>

                            <button className={styles['schedule-apply']} onClick={handleApplyBlock} type="button">
                                {t('appAdmin.RequestSchedule.apply', 'Aplicar horario')}
                            </button>
                        </div>
                    </div>
                    {isBlockApplied && (
                        <div className={styles['schedule-feedback']}>
                            Rango aplicado a {blockDays.length} días.
                        </div>
                    )}
                </div>
            )}

            {tab === 'day' && (
                <div className={styles['schedule-tab-panel']}>
                    <div className={styles['schedule-week']}>
                        <h5>{t('appAdmin.RequestSchedule.dayTitle', 'Editar día por día')}</h5>
                        <p>{t('appAdmin.RequestSchedule.dayDesc', 'Activá cada día y ajustá su horario particular.')}</p>

                        <div className={styles['schedule-day-list']}>
                            {DAYS.map(day => {
                                const config = daySchedule[day.value] || { active: false, start: '09:00', end: '18:00' };
                                return (
                                    <div key={day.value} className={`${styles['schedule-day-row']} ${!config.active ? styles['is-disabled'] : ''}`}>
                                        <label className={styles['schedule-day-switch']}>
                                            <input 
                                                type="checkbox" 
                                                checked={config.active}
                                                onChange={(e) => setDaySchedule({
                                                    ...daySchedule,
                                                    [day.value]: { ...config, active: e.target.checked }
                                                })}
                                            />
                                            <span className={styles['schedule-mini-track']}>
                                                <span className={styles['schedule-mini-thumb']}></span>
                                            </span>
                                            <strong>{day.label}</strong>
                                        </label>

                                        <div className={styles['schedule-day-range']}>
                                            <input 
                                                type="text" 
                                                readOnly
                                                className={`${styles['schedule-time-input']} ${activePicker && (activePicker as any).type === 'dayStart' && (activePicker as any).day === day.value ? styles['is-active'] : ''}`} 
                                                value={config.start}
                                                disabled={!config.active}
                                                onClick={(e) => {
                                                    if (!config.active) return;
                                                    setActivePicker({ type: 'dayStart', day: day.value, rect: e.currentTarget.getBoundingClientRect(), value: config.start });
                                                }}
                                            />
                                            <i>a</i>
                                            <input 
                                                type="text" 
                                                readOnly
                                                className={`${styles['schedule-time-input']} ${activePicker && (activePicker as any).type === 'dayEnd' && (activePicker as any).day === day.value ? styles['is-active'] : ''}`} 
                                                value={config.end}
                                                disabled={!config.active}
                                                onClick={(e) => {
                                                    if (!config.active) return;
                                                    setActivePicker({ type: 'dayEnd', day: day.value, rect: e.currentTarget.getBoundingClientRect(), value: config.end });
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <TimePickerPopover 
                activePicker={activePicker}
                onClose={() => setActivePicker(null)}
                onChange={(val) => {
                    if (!activePicker) return;
                    if (activePicker.type === 'blockStart') setBlockStart(val);
                    else if (activePicker.type === 'blockEnd') setBlockEnd(val);
                    else if (activePicker.type === 'dayStart' && activePicker.day !== undefined) {
                        setDaySchedule({ ...daySchedule, [activePicker.day]: { ...(daySchedule[activePicker.day] || {}), start: val } });
                    }
                    else if (activePicker.type === 'dayEnd' && activePicker.day !== undefined) {
                        setDaySchedule({ ...daySchedule, [activePicker.day]: { ...(daySchedule[activePicker.day] || {}), end: val } });
                    }
                    
                    setActivePicker({ ...activePicker, value: val });
                }}
            />
        </div>
    );
});
